<template>
  <UIPopup
    id="remove-deal-popup"
    v-model="modelValue"
    class="!z-10"
    button-text="Remove"
    closeable
    hide-buttons
  >
    <template v-if="isMobile" #outside>
      <div class="text-subhead-3 m-1 text-white">{{ currentIndex }} / {{ gallery.length }}</div>
      <button class="absolute right-4 top-4" @click="modelValue = false">
        <UIIcon name="big-close" class="text-white" />
      </button>
    </template>
    <template v-if="!isMobile" #header>
      <div class="hidden md:flex items-center justify-between text-left w-full">
        <h4 class="text-headline-4">{{ item.title.en }}</h4>
        <div class="flex items-center gap-4 ml-6">
          <button
            class="flex size-8 items-center justify-center rounded-lg bg-black-05 p-1 hover:bg-black-10 transition-colors cursor-pointer"
            @click="goToPrev"
          >
            <UIIcon name="chevron-big-left" />
          </button>
          <span class="text-subhead-1 black-100">{{ currentIndex }} / {{ gallery.length }}</span>
          <button
            class="flex size-8 items-center justify-center rounded-lg bg-black-05 p-1 hover:bg-black-10 transition-colors cursor-pointer"
            @click="goToNext"
          >
            <UIIcon name="chevron-big-right" />
          </button>
        </div>
      </div>
    </template>
    <template #body>
      <div class="relative -mx-4 md:mx-0">
        <div
          class="absolute left-1/2 right-12 top-1/2 z-10 flex w-full -translate-x-1/2 -translate-y-1/2 justify-between gap-6 px-3 md:hidden"
        >
          <button
            class="flex size-8 items-center justify-center rounded-lg bg-black-05 p-1 hover:bg-black-10 transition-colors cursor-pointer"
            @click="goToPrev"
          >
            <UIIcon name="chevron-big-left" />
          </button>
          <button
            class="flex size-8 items-center justify-center rounded-lg bg-black-05 p-1 hover:bg-black-10 transition-colors cursor-pointer"
            @click="goToNext"
          >
            <UIIcon name="chevron-big-right" />
          </button>
        </div>
        <ClientOnly>
          <Swiper
            v-if="modelValue && gallery.length > 0"
            auto-height
            :modules="[Pagination]"
            :initial-slide="props.initialIndex || 0"
            @swiper="onSwiperInit"
            @slide-change="updateCurrentIndex"
          >
            <SwiperSlide v-for="(image, index) in gallery" :key="index">
              <img
                :src="image"
                :alt="`preview-${index}`"
                class="size-full object-contain md:rounded-xl md:h-[100%] lg:h-[calc(95vh_-_200px)]"
              />
            </SwiperSlide>
          </Swiper>
        </ClientOnly>
      </div>
    </template>
  </UIPopup>
</template>

<script setup lang="ts">
import type { Swiper as ISwiper } from 'swiper'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import type { PropertyDetailed } from '../../types/properties'
import 'swiper/css'
import 'swiper/css/pagination'

type Props = {
  item: PropertyDetailed
  gallery: string[]
  initialIndex?: number
}

const props = defineProps<Props>()

const modelValue = defineModel<boolean>()

const { isMobile } = useAppBreakpoints()
const currentIndex = ref(1)
const swiperInstance = ref<ISwiper | null>(null)

const goToNext = () => {
  if (swiperInstance.value) {
    swiperInstance.value.slideNext()
  }
}

const goToPrev = () => {
  if (swiperInstance.value) {
    swiperInstance.value.slidePrev()
  }
}

const updateCurrentIndex = (swiper: ISwiper) => {
  if (swiper && swiper.el && swiper.el.parentNode && modelValue.value) {
    currentIndex.value = swiper.activeIndex + 1
  }
}

const onSwiperInit = (instance: ISwiper) => {
  swiperInstance.value = instance
  if (props.initialIndex !== undefined && props.initialIndex >= 0) {
    instance.slideTo(props.initialIndex, 0)
    currentIndex.value = props.initialIndex + 1
  }
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (!swiperInstance.value || !modelValue.value) return

  if (event.key === 'ArrowLeft') {
    event.preventDefault()
    swiperInstance.value.slidePrev()
  } else if (event.key === 'ArrowRight') {
    event.preventDefault()
    swiperInstance.value.slideNext()
  }
}

watch(() => modelValue.value, (isOpen) => {
  if (isOpen) {
    currentIndex.value = (props.initialIndex !== undefined && props.initialIndex >= 0) ? props.initialIndex + 1 : 1
    nextTick(() => {
      window.addEventListener('keydown', handleKeyDown)
      if (swiperInstance.value && props.initialIndex !== undefined && props.initialIndex >= 0) {
        swiperInstance.value.slideTo(props.initialIndex, 0)
      }
    })
  } else {
    window.removeEventListener('keydown', handleKeyDown)
    nextTick(() => {
      swiperInstance.value = null
    })
  }
}, { immediate: true })

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown)
  if (swiperInstance.value) {
    try {
      if (swiperInstance.value.el && swiperInstance.value.el.parentNode) {
        swiperInstance.value.destroy(true, true)
      }
    } catch (error) {
    }
    swiperInstance.value = null
  }
})
</script>

<style scoped lang="scss">
.popup {
  :deep() {
    .popup__content {
      width: 100%;
      max-width: 98.333% !important;
      max-height: 83vh;
      overflow: hidden;
      background: #ffffff;
      box-shadow: none;
      border-radius: 12px;

      &-header {
        margin-left: 0;
      }

      &-body {
        @media screen and (max-width: 1023px) {
          padding: 0 !important;
          margin-bottom: 0;
        }
      }
    }
  }
}
</style>
